import { Box, CircularProgress } from '@mui/material';
import _ from 'lodash';
import React from 'react';

import type {
  CampaignListProps,
  NetworkCampaign,
} from '@common/types/campaign';
import { CampaignCardItem } from '@common/components/CampaignCardItem';
import { CampaignPagination } from '@common/components/CampaignPagination';
import { EmptyList } from '@common/components/EmptyList';
import {
  CAMPAIGN_LIST_EMPTY_MESSAGE,
  USER_CAMPAIGN_LIST_EMPTY_MESSAGE,
} from '@common/constants/campaignListMessages';



/**
 * Campaign list component that displays a list of campaigns
 *
 * @param props - The component props
 * @param props.campaigns - The list of campaigns
 * @param props.showUserCampaigns - Whether to show user campaigns
 * @param props.userCampaigns - The list of user campaigns
 * @param props.getCampaignHref - The function to get the campaign href
 * @param props.openCampaignInNewTab - Whether to open the campaign in a new tab
 * @param props.maxCampaigns - The maximum number of campaigns to display
 * @param props.pagination - The pagination
 * @param props.onChangePage - The function to change the page
 * @param props.isPaginationLoading - Whether the pagination is loading
 * @param props.listError - The error message
 * @returns The rendered campaign list
 */
export function CampaignList({
  campaigns,
  showUserCampaigns,
  userCampaigns,
  getCampaignHref,
  openCampaignInNewTab,
  maxCampaigns,
  pagination,
  onChangePage,
  isPaginationLoading,
  listError,
}: CampaignListProps) {
  const showCampaigns: NetworkCampaign[] = showUserCampaigns
    ? userCampaigns
    : campaigns;

  const displayedCampaigns = pagination
    ? showCampaigns
    : showCampaigns.slice(0, maxCampaigns);

  const showPagination = pagination !== undefined && onChangePage !== undefined;

  if (listError) {
    return (
      <EmptyList
        data-test-id="campaign-list-error"
        icon="ERROR"
        message={listError}
        sx={{ marginTop: '20px' }}
      />
    );
  }

  if (
    showUserCampaigns &&
    userCampaigns?.length === 0 &&
    (pagination?.total ?? 0) === 0 &&
    !isPaginationLoading &&
    !listError
  ) {
    return (
      <EmptyList
        icon="CARD_GIFT_CARD"
        message={USER_CAMPAIGN_LIST_EMPTY_MESSAGE}
        sx={{ marginTop: '20px' }}
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {isPaginationLoading && !_.isEmpty(displayedCampaigns) && (
        <Box
          data-test-id="campaign-list-loading-overlay"
          sx={(theme) => ({
            alignItems: 'center',
            backgroundColor:
              theme.colors?.whiteBackdrop ?? 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            inset: 0,
            justifyContent: 'center',
            position: 'absolute',
            zIndex: 1,
          })}
        >
          <CircularProgress color="secondary" />
        </Box>
      )}

      <Box
        data-test-id="campaign-list-selector"
        sx={(theme) => ({
          '& > *': {
            minWidth: 0,
          },
          display: 'grid',
          gap: '40px',
          gridTemplateColumns: 'repeat(3, 1fr)',
          [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
            marginTop: '-2px',
          },
          width: '100%',
        })}
      >
        {!_.isEmpty(displayedCampaigns) ? (
          displayedCampaigns.map((campaign) => (
            <CampaignCardItem
              campaign={campaign}
              href={getCampaignHref(campaign)}
              key={campaign.campaignId}
              openCampaignInNewTab={openCampaignInNewTab}
            />
          ))
        ) : isPaginationLoading ? (
          <Box
            data-test-id="campaign-list-initial-loading"
            sx={{
              alignItems: 'center',
              display: 'flex',
              gridColumn: '1 / -1',
              justifyContent: 'center',
              marginTop: '24px',
              minHeight: '200px',
              width: '100%',
            }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Box
            sx={{
              gridColumn: '1 / -1',
              marginTop: '24px',
              whiteSpace: 'pre-line',
              width: '100%',
            }}
          >
            <EmptyList
              icon="CARD_GIFT_CARD"
              message={
                showUserCampaigns
                  ? USER_CAMPAIGN_LIST_EMPTY_MESSAGE
                  : CAMPAIGN_LIST_EMPTY_MESSAGE
              }
            />
          </Box>
        )}
      </Box>

      {showPagination && (
        <Box sx={{ marginTop: '100px' }}>
          <CampaignPagination
            disabled={isPaginationLoading}
            limit={pagination.limit}
            offset={pagination.offset}
            onChangePage={(page) => {
              void onChangePage?.(page);
            }}
            total={pagination.total}
          />
        </Box>
      )}
    </Box>
  );
}
